import { FunctionComponent, useMemo } from 'react';
import { Box, Text } from 'theme-ui';

const prefix = 'Please describe what you were doing when the error occurred:\n\n\nError Data:\n';

export const ErrorFallback: FunctionComponent<{ error: Error }> = ({ error }) => {
    const obfuscate = useMemo(() => process.env.REACT_APP_OBFUSCATE_ERRORS === 'true', []);
    const obfuscated = useMemo(() => {
        if (obfuscate) {
            return Buffer.from(error.message + '\n' + error.stack, 'utf-8').toString('base64');
        }

        return error.message;
    }, [error, obfuscate]);
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                transform: 'translateY(0)',
                backgroundColor: '#242424',
                color: '#e0e0e0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'all',
                userSelect: 'text',

                a: { color: '#e0e0e0' },
                'a:visited': { color: '#e0e0e0' },
            }}
        >
            <Box
                sx={{
                    width: '95%',
                    maxWidth: '1366px',
                    height: '95%',
                    maxHeight: '1024px',
                    border: '5px solid #e0e0e0',
                    borderRadius: '40px',
                    position: 'relative',
                    overflow: 'hidden',
                    margin: '28px',
                    'z-index': 2,

                    '@media only screen and (max-width: 1000px)': {
                        height: '100%',
                        width: '100%',
                        border: 0,
                        margin: 0,
                    },
                }}
            >
                <Box
                    sx={{
                        overflowWrap: 'break-word',
                        padding: '72px 116px 116px 116px',
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        minHeight: '100%',
                        marginBottom: '116px',
                        '@media only screen and (max-width: 1000px)': {
                            padding: '64px 24px 24px 24px',
                        },

                        '@font-face': {
                            fontFamily: 'Monaco',
                            src: 'url(/assets/webFonts/Monaco.woff) format("woff")',
                        },

                        textAlign: 'center',
                        fontFamily: 'Monaco',
                        fontWeight: 400,
                        fontSize: '18px',
                    }}
                >
                    <Box>
                        <h1>ERROR</h1>
                        {obfuscate ? (
                            <>
                                <Text>An error has occurred. Please refresh the page and try again.</Text>
                                <br />
                                <br />
                                <Text>
                                    If the error happens again, please submit the below Error Code for debugging to the
                                    Game Emulation Team at{' '}
                                    <a
                                        href={`mailto:support@anima.supply?subject=${encodeURIComponent(
                                            'Onlybots Error'
                                        )}&body=${encodeURIComponent(prefix + obfuscated)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        support@anima.supply
                                    </a>
                                    :
                                </Text>
                                <br />
                                <br />
                                <Text
                                    sx={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        fontSize: '10px',
                                        lineHeight: '10px',
                                        marginTop: '36px',
                                        textAlign: 'left',
                                    }}
                                >
                                    {obfuscated}
                                </Text>
                            </>
                        ) : (
                            <pre
                                sx={{ whiteSpace: 'pre-wrap', textAlign: 'left', marginTop: '12px', fontSize: '12px' }}
                            >
                                {error.stack}
                            </pre>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
